/**
 * When the user last did something: typed, clicked, moved the mouse, scrolled
 * or touched the screen. Shared between tabs through localStorage, so working
 * in one tab keeps the others signed in too.
 */

const STORAGE_KEY = 'sabre:last-activity';
// Writing on every mouse move is wasteful; a few seconds of precision is plenty
// against a 15-minute limit.
const WRITE_EVERY_MS = 5_000;

const EVENTS = ['mousedown', 'mousemove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

// 0 until something happens in this page, so a reload does not count as
// activity; the shared value carries the real time across reloads.
let lastLocal = 0;
let lastWritten = 0;

function readShared(): number {
  try {
    const value = Number(window.localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

function writeShared(at: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(at));
  } catch {
    // Private windows may refuse storage; this tab's own clock still works.
  }
}

/** Records activity now. */
export function markActivity(): void {
  const now = Date.now();
  lastLocal = now;
  if (now - lastWritten >= WRITE_EVERY_MS) {
    lastWritten = now;
    writeShared(now);
  }
}

/** The latest activity in any tab, as a timestamp in milliseconds. */
export function lastActivity(): number {
  return Math.max(lastLocal, readShared());
}

/** Listens for activity until the returned function is called. */
export function trackActivity(): () => void {
  const onActivity = () => markActivity();
  for (const name of EVENTS) {
    window.addEventListener(name, onActivity, { passive: true, capture: true });
  }
  return () => {
    for (const name of EVENTS) {
      window.removeEventListener(name, onActivity, { capture: true });
    }
  };
}
